import { format } from "date-fns";
import { useMemo } from "react";

import {
  DeploymentChartType,
  DeploymentTypeFilter,
  DeploymentUsageChartDerivedData,
  DeploymentUsageChartProps,
} from "../types";
import { buildDeploymentChartData, buildDeploymentSeries, filterDeployments } from "../utils";

import { useOrganizationSubscriptionData } from "@/providers";
import { useOrganizationDeploymentUsageData } from "@/queries/organizations";

export const useDeploymentUsageChartData = ({
  chartType,
  dateRange,
  deploymentTypeFilter,
}: DeploymentUsageChartProps & {
  chartType: DeploymentChartType;
  deploymentTypeFilter: DeploymentTypeFilter;
}) => {
  const { isLoadingSubscriptionStatus } = useOrganizationSubscriptionData();
  const startDate = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
  const endDate = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;

  const { data, isLoading, isFetching, isError } = useOrganizationDeploymentUsageData({
    start_date: startDate,
    end_date: endDate,
    isSubscriptionLoading: isLoadingSubscriptionStatus,
  });

  const derivedData = useMemo<DeploymentUsageChartDerivedData>(() => {
    const filteredDeployments = filterDeployments(data?.deployments ?? [], deploymentTypeFilter);
    const deploymentSeries = buildDeploymentSeries(filteredDeployments);
    const chartData = buildDeploymentChartData({
      chartType,
      deployments: filteredDeployments,
      deploymentSeries,
      startDate,
      endDate,
    });
    const totalTokens = filteredDeployments.reduce(
      (sum, deployment) => sum + deployment.total_tokens,
      0,
    );
    const totalSessions = filteredDeployments.reduce(
      (sum, deployment) => sum + deployment.total_sessions,
      0,
    );

    return {
      filteredDeployments,
      deploymentSeries,
      chartData,
      totalTokens,
      totalSessions,
    };
  }, [chartType, data?.deployments, deploymentTypeFilter, endDate, startDate]);

  return {
    ...derivedData,
    isError,
    isChartLoading: isLoading || isFetching,
  };
};
